import Instrument from "shared/games/instruments/Instrument";
import { MixPuzzle, MixPuzzleInstrument } from "shared/games/mixpuzzles/types";
import { difficultySettings } from "../constants";
import { State } from "./reducer";

export interface MixPuzzleInstrumentGrade {
  eq: boolean;
  comp: boolean;
  volume: boolean;
  instrument: Instrument;
  pan: boolean;
}

export interface MixPuzzleGrade {
  correct: boolean;
  mix: { [instrument: string]: MixPuzzleInstrument };
  answer: { [instrument: string]: MixPuzzleInstrument };
  issues: {
    [instrument: string]: MixPuzzleInstrumentGrade;
  };
}

type MixPuzzleGrader = (state: State) => MixPuzzleGrade;

function isEqCorrect({
  attempt,
  desired,
}: {
  attempt: MixPuzzleInstrument;
  desired: MixPuzzleInstrument;
}) {
  if (!attempt || !desired) {
    return true;
  }

  if (+desired.eq_gain === 0) {
    return +desired.eq_gain === +attempt.eq_gain;
  }

  return (
    +desired.eq_gain === +attempt.eq_gain && desired.eq_freq === attempt.eq_freq
  );
}

function isCompCorrect({
  attempt,
  desired,
  state,
}: {
  attempt: MixPuzzleInstrument;
  desired: MixPuzzleInstrument;
  state: State;
}) {
  if (difficultySettings.compression(state) === false) {
    return true;
  }

  return desired.comp === attempt.comp;
}

function isVolumeCorrect({
  attempt,
  desired,
}: {
  attempt: MixPuzzleInstrument;
  desired: MixPuzzleInstrument;
}) {
  return +desired.volume === +attempt.volume;
}

function isPanCorrect({
  attempt,
  desired,
}: {
  attempt: MixPuzzleInstrument;
  desired: MixPuzzleInstrument;
}) {
  return desired.pan === attempt.pan;
}

export function isInstrumentCorrect(params: {
  state: State;
  attempt: MixPuzzleInstrument;
  desired: MixPuzzleInstrument;
}) {
  return (
    isEqCorrect(params) &&
    isCompCorrect(params) &&
    isVolumeCorrect(params) &&
    isPanCorrect(params)
  );
}

const grader: MixPuzzleGrader = (state) => {
  const { mix, mixPuzzle } = state;
  const issues: MixPuzzleGrade["issues"] = {};

  Object.values(mixPuzzle.instruments).forEach((desired) => {
    const attempt = mix[desired.instrument];
    const compIsCorrect = isCompCorrect({ state, attempt, desired });
    const volumeIsCorrect = isVolumeCorrect({ attempt, desired });
    const panIsCorrect = isPanCorrect({ attempt, desired });
    const eqIsCorrect = isEqCorrect({
      attempt,
      desired,
    });

    if (eqIsCorrect && compIsCorrect && volumeIsCorrect && panIsCorrect) {
      return;
    }

    issues[attempt.instrument] = {
      instrument: desired.instrument,
      eq: eqIsCorrect,
      comp: compIsCorrect,
      volume: volumeIsCorrect,
      pan: panIsCorrect,
    };
  });

  return {
    correct: Object.keys(issues).length === 0,
    mix,
    answer: mixPuzzle.instruments,
    issues,
    mixPuzzle,
  };
};

export default grader;
